import BigNumber from 'bignumber.js'
import { capitalize } from 'lodash-es'
import { TAGS_MAP_PARENT_KEY } from '@/constants/assets'
import {
  NO_ALLOCATION_TAG,
  NO_ALLOCATION_TAG_ID,
  NO_CATEGORY_TAG,
  NO_CATEGORY_TAG_ID,
  NO_SEGMENT_TAG,
} from '@/constants/tags'
import i18n from '@/langs'
import { getHash } from './strings'
import { groupTagsByType } from './tags'
import type { RouteLocationRaw } from 'vue-router'
import type { DetailedAccount } from '@/types/services/account'
import type {
  Asset,
  AssetChild,
  AssetChildNFT,
  AccountType,
  CGCategoriesAsset,
  DashboardAsset,
  ICOCurrencyRepr,
  PerpRepr,
  ZeroAsset,
} from '@/types/services/assets'
import type { ICOCurrency } from '@/types/services/global'
import type { Tag, TagTypeValue } from '@/types/services/tags'
import type { AssetShare } from '@/types/utils/assets'

/**
 * Returns the amount of a given child asset. If the child is a zero item,
 * returns 0, otherwise returns the child's amount.
 *
 * @param {AssetChild} child - The child asset to retrieve the amount from.
 * @return {number} The amount of the child asset.
 */
export function getChildAmount(
  child: Pick<AssetChild, 'zero_item' | 'amount'>
): number {
  return child.zero_item ? 0 : child.amount
}

/**
 * Checks if the given account contains any of the specified tags.
 *
 * @param {DetailedAccount | null} account - The account to check for tags.
 * @param {string[]} tagIds - The list of tag IDs to check for.
 * @return {boolean} Returns true if the account contains any of the specified tags, otherwise false.
 */
export function checkIfAccountContainsTags(
  account: DetailedAccount | null,
  tagIds: string[]
): boolean {
  return account?.tag_list.some((tag) => tagIds.includes(tag.tag_id)) || false
}

/**
 * Creates a new ICOCurrency object from an ICOCurrencyRepr object, with the
 * additional properties set to null or empty string.
 *
 * @param {ICOCurrencyRepr} ico - The ICOCurrencyRepr object to convert.
 * @return {ICOCurrency} A new ICOCurrency object with the additional properties set.
 */
export function currencyFromICO(ico: ICOCurrencyRepr): ICOCurrency {
  return {
    ...ico,
    id: '',
    contract_address: null,
    has_image: false,
    last_mcap: null,
    fdv_usd: null,
    platform: null,
    group: 'ico',
    source: '',
    source_id: '',
  }
}

/**
 * Checks if the given object is an ICOCurrency object.
 *
 * @param {unknown} currency - The object to check.
 * @return {currency is ICOCurrency} True if the object is an ICOCurrency, false otherwise.
 */
export function isICOCurrency(currency: unknown): currency is ICOCurrency {
  return (
    typeof currency === 'object' && currency !== null && 'ico_id' in currency
  )
}

/**
 * Checks if the given currency repr is a perpetual position.
 *
 * @param {unknown} currency - The object to check.
 * @return {currency is PerpRepr} True if the object is a PerpRepr.
 */
export function isPerpRepr(currency: unknown): currency is PerpRepr {
  return (
    typeof currency === 'object' &&
    currency !== null &&
    'market_symbol' in currency &&
    'side' in currency
  )
}

/**
 * Returns the route name based on the given account type.
 *
 * @param {AccountType} type - The type of the account.
 * @return {string} The route name corresponding to the account type.
 */
function getRouteNameByType(type: AccountType): string {
  if (type == 'exchange') {
    return 'AssetsExchangesView'
  }
  if (type == 'blockchain') {
    return 'AssetsWalletsView'
  }
  if (type == 'ico') {
    return 'AssetsCustomEntriesView'
  }
  return 'AssetsCryptoAssetsView'
}

/**
 * Returns the detail link based on the account ID and type.
 *
 * @param {string} account_id - The ID of the account.
 * @param {AccountType} account_type - The type of the account.
 * @return {RouteLocationRaw} The detail link object.
 */
export function getDetailLink(
  account_id: string,
  account_type: AccountType
): RouteLocationRaw {
  return {
    name: getRouteNameByType(account_type),
    params: {
      id: account_id,
    },
  }
}

/**
 * Retrieves the currency ID from a currency value. For perps this is a
 * composite key that must match the BE `currency_repr_key`.
 *
 * @param {string | ICOCurrencyRepr | PerpRepr} currency - The currency value.
 * @return {string} The currency ID.
 */
export function getCurrencyId(
  currency: string | ICOCurrencyRepr | PerpRepr
): string {
  if (typeof currency === 'string') return currency
  if (isPerpRepr(currency)) {
    return `perp:${currency.account_id}:${currency.market_symbol}:${currency.side}`
  }
  return currency.ico_id
}

/**
 * Retrieves the currency ID from an asset.
 *
 * @param {Asset | DashboardAsset | ZeroAsset} asset - The asset object.
 * @return {string} The currency ID.
 */
export function getCurrencyIdFromAsset(
  asset: Asset | CGCategoriesAsset | DashboardAsset | ZeroAsset
): string {
  return getCurrencyId(asset.currency)
}

// Getting translations in large loop was too slow (0.5ms/call) so there is the map
const assetTypeTranslations = {
  available: i18n.global.t('ui.assets.asset_types.available'),
  debt: i18n.global.t('ui.assets.asset_types.debt'),
  locked: i18n.global.t('ui.assets.asset_types.locked'),
  claimable: i18n.global.t('ui.assets.asset_types.claimable'),
  illiquid: i18n.global.t('ui.assets.asset_types.illiquid'),
  nft: i18n.global.t('ui.assets.asset_types.nft'),
}

/**
 * Gets a friendly representation of the asset type.
 *
 * @param {string} asset_type - The type of the asset.
 * @return {string} The friendly representation of the asset type.
 */
export function getFriendlyAssetType(asset_type: string): string {
  if (!asset_type) {
    return '-'
  }

  if (asset_type in assetTypeTranslations) {
    return assetTypeTranslations[asset_type]
  }

  return capitalize(asset_type.replace(/_/g, ' ').trim())
}

export function assetShareFromTotal(
  valueMap: Map<string, number>,
  total: number
): AssetShare[] {
  const arr: AssetShare[] = []
  for (const [key, value] of valueMap) {
    const share = total
      ? new BigNumber(value).dividedBy(total).decimalPlaces(4).toNumber()
      : 0
    arr.push({ unit_id: key, share })
  }
  return arr
}

/**
 * Compares two AssetShare objects based on their unit IDs and shares.
 *
 * This function is used for sorting AssetShare objects. It prioritizes
 * NO_ALLOCATION_TAG_ID and NO_CATEGORY_TAG_ID to the end of the sorted list.
 *
 * @param {AssetShare} a - The first AssetShare object to compare.
 * @param {AssetShare} b - The second AssetShare object to compare.
 * @return {number} A negative value if a should come before b, a positive value if b should come before a, and 0 if they are equal.
 */
export function compareAssetShare(a: AssetShare, b: AssetShare): number {
  const to_end_ids = new Set([NO_ALLOCATION_TAG_ID, NO_CATEGORY_TAG_ID])
  if (to_end_ids.has(a.unit_id)) {
    return 1
  }
  if (to_end_ids.has(b.unit_id)) {
    return -1
  }

  return b.share - a.share
}

type AssetInfoChildIdentifier = {
  account_id: string
  source_id: string
  asset_type: string
}

/**
 * Generates a hash for the given account ID, source ID, and asset type.
 *
 * @param {AssetInfoChildIdentifier} child_identifier - The identifier object containing account_id, source_id, and asset_type.
 * @return {string} The generated hash.
 */
export function getChildHash(
  child_identifier: AssetInfoChildIdentifier
): string {
  return getHash(
    `${child_identifier.account_id}-${child_identifier.source_id}-${child_identifier.asset_type}`
  )
}

interface ChildTagsResult {
  tags: Record<TagTypeValue, Tag[]>
  parent_applied: {
    category_tag: boolean
    segment_tag: boolean
    allocation_tag: boolean
  }
}

export function getParentTags(
  assetInfoTagsMap: Record<string, Tag[]>
): Record<TagTypeValue, Tag[]> {
  return groupTagsByType(assetInfoTagsMap[TAGS_MAP_PARENT_KEY] ?? [])
}

export function getTagsForChild(
  child_identifier: AssetInfoChildIdentifier,
  assetInfoTagsMap: Record<string, Tag[]>
): ChildTagsResult {
  const childTags = groupTagsByType(
    assetInfoTagsMap[getChildHash(child_identifier)] ?? []
  )

  const parentTags = getParentTags(assetInfoTagsMap)

  const parent_applied = {
    category_tag: false,
    segment_tag: false,
    allocation_tag: false,
  }

  // Apply parent tags if child doesn't have specific tag types
  for (const tagType of [
    'category_tag',
    'segment_tag',
    'allocation_tag',
  ] as const) {
    if (childTags[tagType].length === 0 && parentTags[tagType].length > 0) {
      childTags[tagType] = [...parentTags[tagType]]
      parent_applied[tagType] = true
    }
  }

  // Apply the 'Other' tag for any type the child still has nothing for
  if (childTags.category_tag.length === 0) {
    childTags.category_tag = [NO_CATEGORY_TAG]
  }
  if (childTags.segment_tag.length === 0) {
    childTags.segment_tag = [NO_SEGMENT_TAG]
  }
  if (childTags.allocation_tag.length === 0) {
    childTags.allocation_tag = [NO_ALLOCATION_TAG]
  }

  return {
    tags: childTags,
    parent_applied,
  }
}

type AssetChangeIdentifier = Pick<
  AssetChild,
  'currency_id' | 'account_id' | 'source_id' | 'asset_type' | 'futures_leg_key'
> & { nft?: AssetChildNFT | string | null }

export function getAssetChangeKey(item: AssetChangeIdentifier): string {
  if (item.futures_leg_key) {
    return JSON.stringify([
      item.currency_id,
      item.account_id,
      item.futures_leg_key,
    ])
  }

  const baseKey = `${item.currency_id}||${item.account_id}||${item.source_id}||${item.asset_type}`

  if (!('nft' in item) || !item.nft) {
    return baseKey
  }

  const nft = item.nft
  const nftName = typeof nft === 'string' ? nft : nft.name

  return nftName ? `${baseKey}||${nftName}` : baseKey
}
